import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Form, FormGroup, Label, Input, Spinner, Alert, Badge } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useGetStoresQuery, useAdjustStockMutation, useGetAdjustmentsQuery } from '../../../../store/services/inventoryApi';
import { Info, List, PlusCircle } from 'react-feather';
import ProductSearch from '../../../Common/ProductSearch';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import moment from 'moment';

const schema = yup.object().shape({
    storeId: yup.string().required('Store is required'),
    productVariantId: yup.string().required('Product is required'),
    quantityChange: yup.number().integer().notOneOf([0], 'Quantity change cannot be zero').required('Quantity is required'),
    type: yup.string().oneOf(['Manual', 'Damage', 'Theft', 'Found', 'Stocktake', 'Other']).required('Adjustment type is required'),
    reason: yup.string().required('Reason is required').min(5, 'Please provide a more detailed reason')
});

const StockAdjustments = () => {
    const navigate = useNavigate();
    const { data: storesData } = useGetStoresQuery();
    const [adjustStock, { isLoading: isAdjusting }] = useAdjustStockMutation();
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const { data: adjustmentsData, isLoading: isLoadingAdjustments, refetch: refetchAdjustments } = useGetAdjustmentsQuery({ page, limit: perPage });


    const [selectedProduct, setSelectedProduct] = useState(null);

    const { control, handleSubmit, register, setValue, formState: { errors }, reset, watch } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            storeId: '',
            productVariantId: '',
            quantityChange: 0,
            type: 'Manual',
            reason: ''
        }
    });

    const storeId = watch('storeId');

    useEffect(() => {
        setSelectedProduct(null);
        setValue('productVariantId', '');
    }, [storeId, setValue]);



    const onSubmit = async (data) => {
        try {
            await adjustStock(data).unwrap();
            toast.success("Stock adjusted successfully");
            reset();
            setSelectedProduct(null);
            refetchAdjustments();
        } catch (err) {
            toast.error(err?.data?.message || "Failed to adjust stock");
        }
    };

    const adjustmentColumns = [
        {
            name: 'Date',
            selector: row => moment(row.createdAt).format('DD/MM HH:mm'),
            sortable: true,
            width: '120px'
        },
        {
            name: 'Product',
            selector: row => row.productVariantId?.productName || 'N/A',
            cell: row => (
                <div className="py-2">
                    <div className="fw-bold">{row.productVariantId?.productName || 'N/A'}</div>
                    <small className="text-muted">SKU: {row.productVariantId?.sku}</small>
                </div>
            )
        },
        {
            name: 'Type',
            selector: row => row.type,
            cell: row => <Badge color="light" className="text-dark border">{row.type}</Badge>,
            width: '100px'
        },
        {
            name: 'Change',
            selector: row => row.quantityChange,
            cell: row => (
                <span className={`fw-bold ${row.quantityChange > 0 ? 'text-success' : 'text-danger'}`}>
                    {row.quantityChange > 0 ? `+${row.quantityChange}` : row.quantityChange}
                </span>
            ),
            width: '80px'
        },
        {
            name: 'Performed By',
            selector: row => row.performedBy?.email,
            grow: 1
        }
    ];

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Stock Adjustments" parent="Inventory" title="Manual Correction" />
            <Container fluid={true}>
                <Row>
                    <Col lg="4" md="12">
                        <Card className="h-100">
                            <CardHeader className="py-3">
                                <h5><PlusCircle size={18} className="me-2 text-primary" /> New Adjustment</h5>
                            </CardHeader>
                            <CardBody>
                                <Alert color="info" className="py-2 small">
                                    <Info size={14} className="me-2" /> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Use this for damage, theft, or counting errors.
                                </Alert>

                                <Form onSubmit={handleSubmit(onSubmit, (errors) => {
                                    const fields = Object.keys(errors).join(', ');
                                    toast.error(`Validation Failed: ${fields}`);
                                })}>
                                    <FormGroup className="mb-3">
                                        <Label className="small fw-bold">Store</Label>
                                        <Controller
                                            name="storeId"
                                            control={control}
                                            render={({ field }) => (
                                                <Input type="select" size="sm" {...field} invalid={!!errors.storeId}>
                                                    <option value="">-- Select --</option>
                                                    {storesData?.data?.map(w => (
                                                        <option key={w._id} value={w._id}>{w.storeName} {w.storeNumber ? `(${w.storeNumber})` : ''}</option>
                                                    ))}
                                                </Input>
                                            )}
                                        />
                                        <div className="invalid-feedback">{errors.storeId?.message}</div>
                                    </FormGroup>

                                    <FormGroup className="mb-3">
                                        <Label className="small fw-bold">Select Product</Label>
                                        <Controller
                                            name="productVariantId"
                                            control={control}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <>
                                                    {!selectedProduct ? (
                                                        <>
                                                            <ProductSearch
                                                                onProductSelect={(variant) => {
                                                                    setSelectedProduct(variant);
                                                                    onChange(variant.productVariantId);
                                                                }}
                                                                placeholder="Search SKUs/Name..."
                                                                storeId={storeId}
                                                                singleSelect={true}
                                                            />
                                                            <div className="invalid-feedback d-block">{error?.message}</div>
                                                        </>
                                                    ) : (
                                                        <div className="p-2 border rounded d-flex justify-content-between align-items-center bg-light">
                                                            <div className="small">
                                                                <div className="fw-bold">{selectedProduct.productName}</div>
                                                                <small className="text-muted">SKU: {selectedProduct.sku}</small>
                                                            </div>
                                                            <Button
                                                                color="link"
                                                                size="xs"
                                                                className="text-danger p-0"
                                                                onClick={() => {
                                                                    setSelectedProduct(null);
                                                                    onChange('');
                                                                }}
                                                            >
                                                                Change
                                                            </Button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </FormGroup>

                                    <Row className="mb-3">
                                        <Col xs="6">
                                            <Label className="small fw-bold">Type</Label>
                                            <Controller
                                                name="type"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="select" size="sm" {...field} invalid={!!errors.type}>
                                                        <option value="Manual">Manual</option>
                                                        <option value="Damage">Damage</option>
                                                        <option value="Theft">Theft</option>
                                                        <option value="Found">Found</option>
                                                        <option value="Other">Other</option>
                                                    </Input>
                                                )}
                                            />
                                        </Col>
                                        <Col xs="6">
                                            <Label className="small fw-bold">Change Qty</Label>
                                            <Controller
                                                name="quantityChange"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="number"
                                                        size="sm"
                                                        {...field}
                                                        invalid={!!errors.quantityChange}
                                                        placeholder="+/-"
                                                    />
                                                )}
                                            />
                                        </Col>
                                    </Row>

                                    <FormGroup className="mb-3">
                                        <Label className="small fw-bold">Reason</Label>
                                        <Controller
                                            name="reason"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="textarea"
                                                    size="sm"
                                                    rows="2"
                                                    {...field}
                                                    invalid={!!errors.reason}
                                                    placeholder="Audit note..."
                                                />
                                            )}
                                        />
                                        <div className="invalid-feedback">{errors.reason?.message}</div>
                                    </FormGroup>

                                    <Button color="primary" block size="sm" type="submit" disabled={isAdjusting}>
                                        {isAdjusting ? <Spinner size="sm" /> : 'Commit Adjustment'}
                                    </Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg="8" md="12">
                        <Card>
                            <CardHeader className="py-3">
                                <h5><List size={18} className="me-2 text-secondary" /> Recent Adjustments</h5>
                            </CardHeader>
                            <CardBody className="p-0">
                                <DataTable
                                    columns={adjustmentColumns}
                                    data={adjustmentsData?.data?.data || []}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={adjustmentsData?.data?.total || 0}
                                    paginationPerPage={perPage}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={newPerPage => setPerPage(newPerPage)}
                                    progressPending={isLoadingAdjustments}
                                    highlightOnHover
                                    responsive
                                    noDataComponent={<div className="p-4 text-center">No recent adjustments.</div>}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default StockAdjustments;
